import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, maskMoneyInput, moneyToInput, parseInputToApi } from '@/lib/money';
import type { MoneyString } from '@/lib/money';

/**
 * Encargos do rotativo — RN-34 e RN-35.
 *
 * ================================================================
 * O SISTEMA CALCULA UM E PERGUNTA OS OUTROS DOIS
 * ================================================================
 *
 * O saldo que rolou é aritmética: total da fatura anterior menos o que foi pago.
 * Vem preenchido, e continua editável — se o banco cobrar sobre outra base, o
 * banco é quem manda.
 *
 * Juros e IOF NÃO são calculados. Cada banco usa uma taxa, capitaliza de um
 * jeito, e o IOF tem parcela diária mais adicional. Um número estimado aqui
 * sairia plausível e errado, que é o pior tipo — seria acreditado.
 *
 * ================================================================
 * O QUE É DESPESA E O QUE NÃO É, DITO NA TELA
 * ================================================================
 *
 * O saldo anterior compõe o que se DEVE, não o resultado do mês: aquela compra
 * já virou despesa quando foi feita. Só juros e IOF são gasto novo.
 *
 * Sem isso escrito, a pessoa que digita R$ 600 de saldo espera ver R$ 600 a mais
 * na despesa do mês, não vê, e conclui que o sistema errou.
 */
export function RevolvingDialog({
  invoice,
  selectedIds,
  onClose,
  onSaved,
}: {
  invoice: {
    id: number;
    reference: string;
    previous_balance: MoneyString;
    suggested_previous_balance: MoneyString;
  };
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // Já informado antes vence a sugestão: quem volta para corrigir vê o que
  // digitou, não o cálculo de novo.
  const inicial =
    invoice.previous_balance !== '0.0000'
      ? invoice.previous_balance
      : invoice.suggested_previous_balance;

  const [saldo, setSaldo] = useState(moneyToInput(inicial));
  const [juros, setJuros] = useState('');
  const [iof, setIof] = useState('');

  const salvar = useMutation({
    mutationFn: () =>
      api.post(
        `/card-invoices/${invoice.id}/rotativo`,
        {
          previous_balance: parseInputToApi(saldo),
          interest: parseInputToApi(juros || '0'),
          iof: parseInputToApi(iof || '0'),
        },
        { entityIds: selectedIds },
      ),
    onSuccess: onSaved,
  });

  const erro = salvar.error instanceof ApiError ? salvar.error : null;
  const temSugestao = invoice.suggested_previous_balance !== '0.0000';

  return (
    <Modal title={`Rotativo da fatura ${invoice.reference}`} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          salvar.mutate();
        }}
        className="space-y-4"
      >
        {erro && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            {erro.message}
          </div>
        )}

        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3.5 py-3 text-sm dark:border-indigo-900/50 dark:bg-indigo-950/30">
          <p className="font-medium text-indigo-900 dark:text-indigo-200">
            O saldo que rolou não é despesa nova.
          </p>
          <p className="mt-1 text-indigo-800/80 dark:text-indigo-300/80">
            Aquelas compras já viraram despesa quando foram feitas — contar de novo dobraria o
            mesmo gasto. O saldo compõe o <strong>total que você deve</strong>. Só{' '}
            <strong>juros e IOF</strong> entram como despesa do mês.
          </p>
        </div>

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
            Saldo da fatura anterior
          </span>
          <input
            value={saldo}
            onChange={(e) => setSaldo(maskMoneyInput(e.target.value))}
            inputMode="numeric"
            className="input w-full"
          />
          <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
            {temSugestao ? (
              <>
                Calculado: {formatMoney(invoice.suggested_previous_balance)} ficaram em aberto na
                fatura anterior. Se o banco disser outro valor, use o do banco.
              </>
            ) : (
              'A fatura anterior está quitada ou não existe — nada rolou automaticamente.'
            )}
          </span>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Juros do rotativo
            </span>
            <input
              value={juros}
              onChange={(e) => setJuros(maskMoneyInput(e.target.value))}
              inputMode="numeric"
              placeholder="0,00"
              className="input w-full"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              IOF
            </span>
            <input
              value={iof}
              onChange={(e) => setIof(maskMoneyInput(e.target.value))}
              inputMode="numeric"
              placeholder="0,00"
              className="input w-full"
            />
          </label>
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400">
          Copie juros e IOF da fatura do banco. O sistema não os estima: cada banco usa uma taxa e
          o IOF tem parcela diária mais adicional — um número inventado aqui pareceria certo e
          estaria errado.
        </p>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={salvar.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:opacity-50"
          >
            {salvar.isPending ? 'Salvando…' : 'Registrar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/** Mesma casca do diálogo de pagamento, para as duas telas se parecerem. */
function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 transition hover:text-slate-600 dark:hover:text-slate-200"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
